/**
 * Message analysis dot state, derived purely from the message-row status
 * column (STK-357). Values mirror Python MessageStatusType / AnalysisStatus:
 * QUEUED(5)=waiting, PROCESSING(1)=analyzing, COMPLETED(2)=analyzed,
 * FAILED(3)=failed. PENDING(0), SEGMENTATION_IGNORED(4) and outbound
 * messages render no dot.
 */
export type AnalysisPillState = "waiting" | "analyzing" | "analyzed" | "failed"

export const MESSAGE_ROW_STATUS_ANALYZING = 1

const STATUS_TO_PILL: Record<number, AnalysisPillState> = {
  5: "waiting",
  1: "analyzing",
  2: "analyzed",
  3: "failed",
}

export function resolveAnalysisPillState(params: {
  rowStatus: number | null | undefined
  isInbound: boolean
}): AnalysisPillState | null {
  if (!params.isInbound) return null
  if (params.rowStatus == null) return null
  return STATUS_TO_PILL[params.rowStatus] ?? null
}

/**
 * True when a message is "stuck" — analysis is queued forever (QUEUED(5), the
 * env-misconfig / dead-run case of STK-1741) or has failed (FAILED(3)) — and a
 * force-re-analyze affordance should be offered (STK-1878). Deliberately NOT
 * offered on `analyzing` (a live claim), `analyzed` (already done), or `null`
 * (no dot: outbound, PENDING(0), SEGMENTATION_IGNORED(4)) so the action stays
 * focused on the messages an operator actually needs to unblock.
 *
 * `conversationHasLiveAnalysis` separates the two populations that share
 * QUEUED(5). The per-conversation queue in `treatMessage` parks a message
 * behind whichever caller took the lock and returns `queued` WITHOUT moving
 * the row off QUEUED — unlike every other early exit, which writes PENDING(0).
 * So a message that merely lost the queue race looks identical to one whose
 * run died, and offering "re-analyze" there would mint a redundant execution
 * against a conversation that is already being analysed. A sibling sitting at
 * ANALYZING(1) is exactly the evidence that a live run will drain this
 * message, so the affordance is withheld until that run finishes.
 *
 * `failed` is never suppressed: a live sibling run drains the queue, it does
 * not retry a message the previous run already resolved as FAILED(3).
 */
export function isReanalyzableMessage(
  state: AnalysisPillState | null | undefined,
  options?: { conversationHasLiveAnalysis?: boolean }
): boolean {
  if (state === "failed") return true
  if (state !== "waiting") return false
  return !options?.conversationHasLiveAnalysis
}
